import { createContext, FC, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

interface ContextProps {
  swipedSymbol: Symbol | null;
  setSwipedSymbol: Function;
}

const SwipeContext = createContext({} as ContextProps);

export const useSwipe = (
  symbol: Symbol,
  { swipeRight, swipeLeft, swipeUp, swipeDown }: any
) => {
  const swipe = useContext(SwipeContext);
  useEffect(() => {
    let xStart: number, yStart: number;

    function move(x: number, y: number) {
      if (swipe.swipedSymbol && swipe.swipedSymbol !== symbol) return;

      const xDiff = x - xStart;
      const yDiff = y - yStart;

      if (Math.abs(xDiff) > Math.abs(yDiff)) {
        // Horizontal swipe
        if (xDiff > 0) {
          // Right
          if (!swipeRight) return;
          swipeRight();
        } else {
          // Left
          if (!swipeLeft) return;
          swipeLeft();
        }
      } else {
        // Vertical swipe
        if (yDiff > 0) {
          // Down
          if (!swipeDown) return;
          swipeDown();
        } else {
          // Up
          if (!swipeUp) return;
          swipeUp();
        }
      }
    }

    function handleMouseDown(e: MouseEvent) {
      xStart = e.clientX;
      yStart = e.clientY;
    }

    function handleMouseUp(e: MouseEvent) {
      if (!xStart || !yStart) return;

      move(e.clientX, e.clientY);
    }

    function handleTouchStart(e: TouchEvent) {
      const firstTouch = e.touches[0];
      xStart = firstTouch.clientX;
      yStart = firstTouch.clientY;
    }

    function handleTouchMove(e: TouchEvent) {
      if (!xStart || !yStart) return;

      const firstTouch = e.touches[0];
      move(firstTouch.clientX, firstTouch.clientY);
    }

    window.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("touchstart", handleTouchStart);
    window.addEventListener("touchmove", handleTouchMove);

    return () => {
      window.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
    };
  }, [swipeRight, swipeLeft, swipeUp, swipeDown, symbol, swipe]);

  return swipe;
};

const SwipeProvider: FC = ({ children }) => {
  const [swipedSymbol, setSwipedSymbol] = useState<Symbol | null>(null);
  const location = useLocation();

  const value = {
    swipedSymbol,
    setSwipedSymbol,
  };

  useEffect(() => {
    setSwipedSymbol(null);
  }, [location]);

  return (
    <SwipeContext.Provider value={value}>{children}</SwipeContext.Provider>
  );
};

export default SwipeProvider;
